import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { generalConfigApi, timeKeepingApi, userApi } from '../../../apis';
import {
  EmployeeRelatedInformationDtoRolesEnum,
  GetEmployeeTimekeepingHistoryDto,
  GetEmployeeTimekeepingHistoryDtoRequestDayOffEnum,
  TimekeepingReportTypeEnum,
  UpdateEmployeeTimekeeping,
} from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import DatePickerWrap from '../../../components/DatePickerWrap';
import SvgCsvGreen from '../../../components/svg/SvgCsvGreen';
import SvgEdit from '../../../components/svg/SvgEdit';
import TableWrap from '../../../components/TableWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import { MONTH_YEAR_FORMAT_JP, YEAR_MONTH_DATE_FORMAT_JP, YEAR_MONTH_FORMAT } from '../../../constants/common';
import { DAY_START, QUERY_KEY, TIME_KEEPING_API } from '../../../constants/QueryKey';
import { ADMIN_ROUTE_NAME } from '../../../constants/RouteName';
import type { RootState } from '../../../store';
import { downloadCSV, formatMinutesToHoursAndMinutes } from '../../../utils';
import AttendanceOpTion from './AttendanceOption';
import './style.scss';
import ModalTimeKeeping from '../../../components/ModalTimekeeping';

export const n = (key: keyof Omit<UpdateEmployeeTimekeeping, 'workType'>) => key;

const AttendanceConfirm = () => {
  const intl = useIntl();

  const [form] = Form.useForm();
  const month = Form.useWatch('month', form);

  const { employeeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.auth.authUser);
  const isManagerRoute = location.pathname.includes(ADMIN_ROUTE_NAME.TIMEKEEPING_CONFIRMATION);
  const id = isManagerRoute ? user?.id : employeeId;

  const [timeKeepingHistory, setTimeKeepingHistory] = useState<GetEmployeeTimekeepingHistoryDto>();

  const columns = [
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.date' }),
      dataIndex: 'date',
      key: 'date',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">{dayjs(record).format(YEAR_MONTH_DATE_FORMAT_JP)}</p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.attendance' }),
      dataIndex: 'dateType',
      key: 'dateType',
      render: (value, record) => {
        let messageId = value ? `attendance.confirm.dateType.${value}` : '';
        let additionalMessage = '';
        if (record.requestDayOff === GetEmployeeTimekeepingHistoryDtoRequestDayOffEnum.Other) {
          messageId += '_LEAVE';
          const requestDayOffReport = record.reports?.find((rp) => rp.type === TimekeepingReportTypeEnum.RequestDayOff);
          additionalMessage = requestDayOffReport?.option || '';
        }
        return (
          <p className="employee-option_table-column-data">
            {additionalMessage || (intl.messages?.[messageId] ? intl.formatMessage({ id: messageId }) : '')}
          </p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.startTime' }),
      dataIndex: 'start',
      key: 'start',
      render: (record) => {
        return <p className="employee-option_table-column-data">{record == null ? '' : record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.endTime' }),
      dataIndex: 'end',
      key: 'end',
      render: (record) => {
        return <p className="employee-option_table-column-data">{record == null ? '' : record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.breakTime' }),
      dataIndex: 'breakTime',
      key: 'breakTime',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.regular' }),
          }}
        />
      ),
      dataIndex: 'actualTimeWorking',
      key: 'actualTimeWorking',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.normalOvertime' }),
          }}
        />
      ),
      dataIndex: 'overtimeDay',
      key: 'overtimeDay',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.lateNight' }),
          }}
        />
      ),
      dataIndex: 'overtimeNight',
      key: 'overtimeNight',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.accumulated' }),
          }}
        />
      ),
      dataIndex: 'stackOvertime',
      key: 'stackOvertime',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    ...(!employeeId
      ? [
          {
            title: '',
            dataIndex: 'option',
            key: 'option',
            render: (record, row) => {
              return (
                <ButtonWrap
                  onClick={() => setTimeKeepingHistory({ ...row, userId: id as string })}
                  className="attendance-confirm_table-column-button"
                >
                  <SvgEdit />
                  {intl.formatMessage({ id: 'attendance.confirm.button.edit' })}
                </ButtonWrap>
              );
            },
          },
        ]
      : []),
  ];

  const { data: userData } = useQuery({
    queryKey: [QUERY_KEY.USER_BY_ID, employeeId],
    queryFn: () => userApi.userControllerGetUserById(employeeId as string),
    enabled: !!employeeId,
  });

  const { data: dayStartData } = useQuery({
    queryKey: [DAY_START],
    queryFn: () => generalConfigApi.generalConfigControllerConfigWorkSchedule(),
  });

  const {
    data: attendanceData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [TIME_KEEPING_API, employeeId, month],
    queryFn: () =>
      timeKeepingApi.timekeepingControllerGetEmployeeTimekeepingHistory(
        id as string,
        month.format(MONTH_YEAR_FORMAT_JP),
        1,
        undefined,
      ),
    enabled: !!month,
  });

  const exportCsv = useMutation({
    mutationFn: async () => {
      return await timeKeepingApi.timekeepingControllerExportTimekeepingToCsv(
        id as string,
        month.format(MONTH_YEAR_FORMAT_JP),
        1,
        undefined,
      );
    },
    onSuccess: (data: any) => {
      const filename = employeeId
        ? `勤怠管理_${userData?.data?.data?.name}_${dayjs(month).format(YEAR_MONTH_FORMAT)}.csv`
        : `勤怠確認_${user?.name}_${dayjs(month).format(YEAR_MONTH_FORMAT)}.csv`;
      downloadCSV(data?.data?.data, filename);
      ToastSuccess({
        content: intl.formatMessage({ id: 'common.csv.export.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'common.csv.export.failed' }),
      });
    },
  });

  useEffect(() => {
    if (user?.role && user?.role !== EmployeeRelatedInformationDtoRolesEnum.Manager) {
      navigate(-1);
    }
  }, [navigate, user?.role]);

  useEffect(() => {
    if (dayStartData?.data?.data?.dayStartOfMonth) {
      const getCurrentPayrollMonth = (dayStartOfMonth: number) => {
        const today = dayjs();
        const currentDay = today.date();

        const startDay = dayStartOfMonth || 2;

        if (currentDay >= startDay) {
          form.setFieldValue('month', dayjs().add(1, 'month'));
        } else {
          form.setFieldValue('month', dayjs());
        }
      };

      const dayStartOfMonth = dayStartData?.data?.data?.dayStartOfMonth;
      getCurrentPayrollMonth(dayStartOfMonth);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayStartData]);

  const handleExport = () => {
    exportCsv.mutate();
  };

  const handleSubmit = () => {
    setTimeKeepingHistory(undefined);
    refetch();
  };

  return (
    <div className="attendance-confirm">
      <Widget
        breadcrumbs={[
          employeeId
            ? intl.formatMessage({ id: 'attendance.confirm.breadcumb' }, { name: userData?.data?.data?.name })
            : intl.formatMessage({ id: 'attendance.confirm.breadcumb.manager' }, { name: user?.name }),
        ]}
        content={
          <TableWrap
            isPagination={false}
            loading={isLoading}
            leftTopContent={
              <Form form={form}>
                <Form.Item name={'month'}>
                  <DatePickerWrap picker="month" format={MONTH_YEAR_FORMAT_JP} allowClear={false} width={312} />
                </Form.Item>
              </Form>
            }
            middleContent={
              <>
                <AttendanceOpTion initialFilter={{ month }} />
              </>
            }
            rightTopContent={
              <div className="attendance-confirm__right-export" onClick={handleExport}>
                <SvgCsvGreen /> <span>{intl.formatMessage({ id: 'common.csv.export.title' })}</span>
              </div>
            }
            tableProps={{
              dataSource: attendanceData?.data.data?.content,
              columns,
              className: 'attendance-confirm_table',
              loading: isLoading,
            }}
          />
        }
        title={
          employeeId ? userData?.data?.data?.name : user?.name || intl.formatMessage({ id: 'attendance.confirm.title' })
        }
      />

      {!!timeKeepingHistory && (
        <ModalTimeKeeping
          open={!!timeKeepingHistory}
          timeKeepingHistory={timeKeepingHistory}
          onCancel={() => setTimeKeepingHistory(undefined)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};
export default AttendanceConfirm;
